import type { JSX } from 'preact';

/** Inline icon set, ported from design/design-handoff mockup <symbol> defs.
 *  All icons share a 24-viewBox, 1.75 stroke, round caps/joins. */
const PATHS: Record<string, JSX.Element> = {
  mic: (
    <g>
      <rect x="9" y="3" width="6" height="12" rx="3" />
      <path d="M5 11a7 7 0 0 0 14 0" />
      <line x1="12" y1="18" x2="12" y2="22" />
      <line x1="8" y1="22" x2="16" y2="22" />
    </g>
  ),
  menu: (
    <g>
      <line x1="4" y1="7" x2="20" y2="7" />
      <line x1="4" y1="12" x2="20" y2="12" />
      <line x1="4" y1="17" x2="14" y2="17" />
    </g>
  ),
  history: (
    <g>
      <circle cx="12" cy="12" r="9" />
      <polyline points="12 7 12 12 15 14" />
    </g>
  ),
  sun: (
    <g>
      <circle cx="12" cy="12" r="4" />
      <path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M4.93 19.07l1.41-1.41M17.66 6.34l1.41-1.41" />
    </g>
  ),
  moon: <path d="M21 12.5A9 9 0 1 1 11.5 3a7 7 0 0 0 9.5 9.5z" />,
  clock: (
    <g>
      <circle cx="12" cy="12" r="9" />
      <polyline points="12 7 12 12 16 14" />
    </g>
  ),
  'clock-slash': (
    <g>
      <circle cx="12" cy="12" r="9" />
      <polyline points="12 7 12 12 16 14" />
      <line x1="4" y1="4" x2="20" y2="20" />
    </g>
  ),
  camera: (
    <g>
      <path d="M3 8h3l2-3h8l2 3h3v11H3z" />
      <circle cx="12" cy="13" r="3.5" />
    </g>
  ),
  paw: (
    <g>
      <ellipse cx="6" cy="11" rx="2" ry="2.5" />
      <ellipse cx="18" cy="11" rx="2" ry="2.5" />
      <ellipse cx="9" cy="6" rx="2" ry="2.5" />
      <ellipse cx="15" cy="6" rx="2" ry="2.5" />
      <path d="M8 16c0-3 2-4 4-4s4 1 4 4-2 4-4 4-4-1-4-4z" />
    </g>
  ),
  cal: (
    <g>
      <rect x="3" y="5" width="18" height="16" rx="2" />
      <line x1="3" y1="10" x2="21" y2="10" />
      <line x1="8" y1="3" x2="8" y2="7" />
      <line x1="16" y1="3" x2="16" y2="7" />
    </g>
  ),
  chat: <path d="M21 12a8 8 0 0 1-11.5 7.2L3 21l1.8-6.5A8 8 0 1 1 21 12z" />,
  grid: (
    <g>
      <rect x="3" y="3" width="7" height="7" rx="1.5" />
      <rect x="14" y="3" width="7" height="7" rx="1.5" />
      <rect x="3" y="14" width="7" height="7" rx="1.5" />
      <rect x="14" y="14" width="7" height="7" rx="1.5" />
    </g>
  ),
  search: (
    <g>
      <circle cx="11" cy="11" r="7" />
      <line x1="16.5" y1="16.5" x2="21" y2="21" />
    </g>
  ),
  plus: (
    <g>
      <line x1="12" y1="5" x2="12" y2="19" />
      <line x1="5" y1="12" x2="19" y2="12" />
    </g>
  ),
  check: <polyline points="4.5 12.5 9.5 17.5 19.5 7" />,
  x: (
    <g>
      <line x1="6" y1="6" x2="18" y2="18" />
      <line x1="18" y1="6" x2="6" y2="18" />
    </g>
  ),
  'chevron-right': <polyline points="9 5 16 12 9 19" />,
  'chevron-left': <polyline points="15 5 8 12 15 19" />,
  'chevron-down': <polyline points="5 9 12 16 19 9" />,
  bell: (
    <g>
      <path d="M6 10a6 6 0 1 1 12 0c0 4 1.5 5.5 2 6H4c.5-.5 2-2 2-6z" />
      <path d="M10 19a2 2 0 0 0 4 0" />
    </g>
  ),
  'bell-snooze': (
    <g>
      <path d="M6 10a6 6 0 1 1 12 0c0 4 1.5 5.5 2 6H4c.5-.5 2-2 2-6z" />
      <path d="M10 19a2 2 0 0 0 4 0" />
      <path d="M10 8h4l-4 4h4" />
    </g>
  ),
  photo: (
    <g>
      <rect x="3" y="4" width="18" height="16" rx="2" />
      <circle cx="9" cy="10" r="2" />
      <path d="M3 17l5-4 4 3 4-4 5 5" />
    </g>
  ),
  doc: (
    <g>
      <path d="M6 2h8l5 5v15H6z" />
      <path d="M14 2v5h5" />
      <line x1="9" y1="12" x2="16" y2="12" />
      <line x1="9" y1="16" x2="16" y2="16" />
    </g>
  ),
  gear: (
    <g>
      <circle cx="12" cy="12" r="3.2" />
      <path d="M12 2.5l1.2 2.6 2.8-.5 1 2.7 2.7 1-.5 2.8 2.3 1.9-1.6 2.4.9 2.7-2.6 1.2-.2 2.9-2.9-.2-1.9 2.2-2.4-1.6-2.7.9-1.2-2.6-2.9-.2.2-2.9-2.2-1.9 1.6-2.4-.9-2.7 2.6-1.2.2-2.9 2.9.2 1.9-2.2z" />
    </g>
  ),
  scale: (
    <g>
      <path d="M12 3v18" />
      <path d="M5 7h14" />
      <path d="M5 7l-2.5 6a3 3 0 0 0 5 0z" />
      <path d="M19 7l-2.5 6a3 3 0 0 0 5 0z" />
      <path d="M8 21h8" />
    </g>
  ),
  share: (
    <g>
      <path d="M12 3v12" />
      <polyline points="8 7 12 3 16 7" />
      <path d="M5 11v8a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-8" />
    </g>
  ),
  copy: (
    <g>
      <rect x="9" y="9" width="12" height="12" rx="2" />
      <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
    </g>
  ),
  speaker: (
    <g>
      <path d="M4 9v6h4l5 4V5L8 9z" />
      <path d="M16 9a4 4 0 0 1 0 6" />
      <path d="M18.5 6.5a8 8 0 0 1 0 11" />
    </g>
  ),
  'speaker-off': (
    <g>
      <path d="M4 9v6h4l5 4V5L8 9z" />
      <line x1="17" y1="9" x2="22" y2="15" />
      <line x1="22" y1="9" x2="17" y2="15" />
    </g>
  ),
  send: <path d="M3 11.5l18-8-8 18-2.5-7.5z" />,
  undo: (
    <g>
      <polyline points="8 5 3 10 8 15" />
      <path d="M3 10h11a6 6 0 0 1 0 12h-3" />
    </g>
  ),
  leaf: <path d="M5 19C5 9 13 4 20 4c0 7-3 15-13 15-1 0-2 0-2 0zM5 19c2-5 5-8 9-10" />,
  'wifi-off': (
    <g>
      <path d="M1 5a16 16 0 0 1 11-4" />
      <path d="M5 9a11 11 0 0 1 4.5-2.5" />
      <path d="M8.5 12.5a6.5 6.5 0 0 1 2-1.2" />
      <circle cx="12" cy="17" r="1" fill="currentColor" stroke="none" />
      <line x1="2" y1="2" x2="22" y2="22" />
      <path d="M14 4.5A16 16 0 0 1 23 9" />
    </g>
  ),
  edit: (
    <g>
      <path d="M4 20l1-4L17 4l3 3-12 12z" />
      <line x1="14" y1="7" x2="17" y2="10" />
    </g>
  ),
  trash: (
    <g>
      <path d="M4 7h16" />
      <path d="M9 7V4h6v3" />
      <path d="M6 7l1 14h10l1-14" />
    </g>
  ),
  dove: (
    <g>
      <path d="M12 6c1-2 3-3 5-3-1 2 0 3 2 3l3 1-3 2c0 6-5 10-11 10H3l3-3c-2-1-3-3-3-6 3 0 5 1 6 3 0-3 1-6 3-7z" />
      <circle cx="15.5" cy="6.5" r=".5" fill="currentColor" stroke="none" />
    </g>
  ),
  sparkle: (
    <g>
      <path d="M12 3l1.8 5.2L19 10l-5.2 1.8L12 17l-1.8-5.2L5 10l5.2-1.8z" />
      <path d="M19 16l.8 2.2L22 19l-2.2.8L19 22l-.8-2.2L16 19l2.2-.8z" />
    </g>
  ),
  refresh: (
    <g>
      <path d="M20 11a8 8 0 1 0-2.3 6" />
      <polyline points="20 5 20 11 14 11" />
    </g>
  ),
  download: (
    <g>
      <path d="M12 3v12" />
      <polyline points="8 11 12 15 16 11" />
      <path d="M5 15v4a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-4" />
    </g>
  ),
};

export type IconName = keyof typeof PATHS & string;

export function Icon({ name, size = 24, class: cls }: { name: IconName; size?: number; class?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      width={size}
      height={size}
      class={cls}
      fill="none"
      stroke="currentColor"
      stroke-width="1.75"
      stroke-linecap="round"
      stroke-linejoin="round"
      aria-hidden="true"
    >
      {PATHS[name] ?? null}
    </svg>
  );
}
